import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/form-field';
import { apiForgotPassword } from '@/features/users/api';
import { extractErrorMessage } from '@/lib/api-client';

const schema = z.object({
  email: z.string().email('Informe um e-mail válido'),
});

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  if (sent) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-success/10 text-success">
          <CheckCircle className="size-7" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold">Verifique seu email</h2>
          <p className="text-sm text-muted-foreground">
            Se esse email estiver cadastrado, você receberá um link para redefinir sua senha.
          </p>
        </div>
        <Button asChild variant="outline" className="w-full">
          <Link to="/login"><ArrowLeft className="size-4" /> Voltar ao login</Link>
        </Button>
      </div>
    );
  }

  const onSubmit = async (values: z.infer<typeof schema>) => {
    setError(null);
    try {
      await apiForgotPassword(values.email);
      setSent(true);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <div className="space-y-7">
      <div className="space-y-2">
        <h2 className="text-3xl font-semibold tracking-tight">Esqueceu a senha?</h2>
        <p className="text-sm text-muted-foreground">
          Informe seu e-mail e enviaremos um link para redefinir sua senha.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <FormField control={form.control} name="email" label="E-mail" type="email" placeholder="voce@empresa.com.br" autoFocus />
        <Button type="submit" size="lg" className="w-full" loading={form.formState.isSubmitting}>
          <Mail className="size-4" /> Enviar link
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        Lembrou a senha?{' '}
        <Link to="/login" className="font-medium text-primary hover:underline">Voltar ao login</Link>
      </p>
    </div>
  );
}
